import { ClientOngoingLoad, ClientUser, DeliveryStatus, DriverEmploymentStatus, IRoute, OngoingLoadStatus, Truck } from '@numeo/types-core'
import { isValid, parseISO } from 'date-fns'
import JSZip from 'jszip'
import { formatDeliveryStatus, getDeliveryStatusColor, getDeliveryStatusLabel } from 'utils/delivery-status'
import { getTimezone, STATE_TIMEZONE_MAP } from 'utils/timezone-utils'
import { ongoingLoadsApi } from '../api/ongoing-loads.api'
import { TabType } from '../types'

// Re-export for backward compatibility
export { getDeliveryStatusLabel, getDeliveryStatusColor as getStatusColor }

export function formatInState(dateUTC: Date | string | null, state: string, fmt: Intl.DateTimeFormatOptions = {}) {
    if (!dateUTC) return '--'
    const d = typeof dateUTC === 'string' ? new Date(dateUTC) : dateUTC
    if (isNaN(d.getTime())) return '--'
    const tz = STATE_TIMEZONE_MAP[state] || 'America/New_York'

    // DO NOT call toZonedTime here
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: tz,
        timeZoneName: 'short',
        ...fmt,
    }).format(d)
}

// Helper to get a readable status name
export const getStatusDisplayName = (status?: DeliveryStatus, isLate: boolean = false): string | null => {
    if (!status) return null
    return isLate ? 'Delayed' : `${formatDeliveryStatus(status.status)} (${status.stopIndex + 1})`
}

/**
 * @deprecated Use getTimezone() with coordinates for accurate results
 * Legacy state-to-timezone mapping - inaccurate for 900+ edge case cities
 */
export const timezoneMap: { [key: string]: string } = {
    AL: 'America/Chicago',
    AK: 'America/Anchorage',
    AZ: 'America/Phoenix',
    AR: 'America/Chicago',
    CA: 'America/Los_Angeles',
    CO: 'America/Denver',
    CT: 'America/New_York',
    DE: 'America/New_York',
    FL: 'America/New_York',
    GA: 'America/New_York',
    HI: 'Pacific/Honolulu',
    ID: 'America/Boise',
    IL: 'America/Chicago',
    IN: 'America/Indiana/Indianapolis',
    IA: 'America/Chicago',
    KS: 'America/Chicago',
    KY: 'America/New_York',
    LA: 'America/Chicago',
    ME: 'America/New_York',
    MD: 'America/New_York',
    MA: 'America/New_York',
    MI: 'America/Detroit',
    MN: 'America/Chicago',
    MS: 'America/Chicago',
    MO: 'America/Chicago',
    MT: 'America/Denver',
    NE: 'America/Chicago',
    NV: 'America/Los_Angeles',
    NH: 'America/New_York',
    NJ: 'America/New_York',
    NM: 'America/Denver',
    NY: 'America/New_York',
    NC: 'America/New_York',
    ND: 'America/Chicago',
    OH: 'America/New_York',
    OK: 'America/Chicago',
    OR: 'America/Los_Angeles',
    PA: 'America/New_York',
    RI: 'America/New_York',
    SC: 'America/New_York',
    SD: 'America/Chicago',
    TN: 'America/Chicago',
    TX: 'America/Chicago',
    UT: 'America/Denver',
    VT: 'America/New_York',
    VA: 'America/New_York',
    WA: 'America/Los_Angeles',
    WV: 'America/New_York',
    WI: 'America/Chicago',
    WY: 'America/Denver',
    DC: 'America/New_York',
}

export const formatDateTimeWithTimezone = (
    date: Date | null,
    state: string,
    latitude?: number | null,
    longitude?: number | null,
    monthLength: 'short' | 'long' = 'long'
) => {
    if (!date) return '--'
    if (isNaN(new Date(date).getTime())) return '--'

    const timezone = getTimezone(latitude, longitude, state)

    return new Date(date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: monthLength,
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
        timeZoneName: 'short',
        hour12: true,
    })
}
/** Shared with the communications RouteStops. True when a stop time string is a real window ("08-10"), false for empty, a single time or equal ends ("08-08"). */
export const isTimeRange = (time?: string | null): boolean => {
    if (!time) return false
    const parts = time.replace(/\s/g, '').split('-')
    if (parts.length !== 2) return false
    return parts[0] !== parts[1]
}

type DateTimeParts = {
    calendarDate: string
    dateLabel: string // "Fri, Mar 28"
    timeLabel: string // "3:00 PM"
    tz: string // "EDT"
    full: string // "Fri, Mar 28 at 3:00 PM EDT"
    numericDate: string // "03/28"
}
/** "Mar" -> "03". Keyed off the `month: 'short'` output of the formatter below. */
const SHORT_MONTH_TO_NUMBER: Record<string, string> = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12',
}

const getDateTimePartsWithTimezone = (
    date: Date | string | null,
    state?: string,
    latitude?: number | null,
    longitude?: number | null
): DateTimeParts | null => {
    if (!date) return null

    let d: Date
    if (date instanceof Date) {
        d = date
    } else if (typeof date === 'string') {
        d = parseISO(date)
    } else {
        return null
    }
    if (!isValid(d)) return null

    const timeZone = getTimezone(latitude, longitude, state)
    const formatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone,
        timeZoneName: 'short',
    })

    const parts = formatter.formatToParts(d)
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
    const weekday = get('weekday') // "Fri"
    const month = get('month') // "Mar"
    const day = get('day') // "28"
    const hour = get('hour') // "3"
    const minute = get('minute') // "00"
    const dayPeriod = get('dayPeriod') // "PM"
    const tz = get('timeZoneName') // "EDT"

    const dateLabel = `${weekday}, ${month} ${day}`.trim() // "Fri, Mar 28"
    const timeLabel = `${hour}:${minute} ${dayPeriod}`.trim() // "3:00 PM"
    const full = `${dateLabel} at ${timeLabel} ${tz}`.trim() // "Fri, Mar 28 at 3:00 PM EDT"
    // "03/28" — the numeric form the redesign's stop cells use. Derived from the parts this
    // formatter already produced rather than from a second Intl.DateTimeFormat: this runs per stop,
    // per row, on every render of a virtualised table, and a second instance here would double that.
    const numericDate = month && day ? `${SHORT_MONTH_TO_NUMBER[month] ?? month}/${day.padStart(2, '0')}` : ''
    const calendarDate = `${get('year')}-${month}-${day}`
    return { calendarDate, dateLabel, timeLabel, tz, full, numericDate }
}

/**
 * Both forms come back from one call, because building them costs two `Intl.DateTimeFormat`
 * instances and this runs per stop, per row, on every render of a virtualised table.
 *
 * `singleLine` is the long form — "Thu, Jul 16 at 8:00 AM - 10:00 AM EDT" — which the classic board
 * renders and must keep rendering. It wants 238px at 13px type and the redesigned board's stop
 * column has 180, so there it was clipping to "…8:00 AM - 10:00 AM E…" and losing the end of the
 * appointment window: the one number in the line a dispatcher is reading it for.
 *
 * `compactLine` spends those pixels on data instead of on grammar — the weekday follows from the
 * date, "at" is punctuation, and a meridiem repeated on both ends of a same-half-day window is
 * printed once. "Jul 16, 8:00 - 10:00 AM EDT" fits, and the long form stays in the tooltip.
 */
export function formatArrivalDeadlineRange(
    arrivalDate?: Date | string | null,
    deadlineDate?: Date | string | null,
    stateCode?: string,
    latitude?: number | null,
    longitude?: number | null
): { sameDay: boolean; singleLine?: string; compactLine?: string; windowLine?: string } {
    if (!arrivalDate || !deadlineDate) return { sameDay: false }
    // Parse/validate with date-fns
    const parseToDate = (value: Date | string | null | undefined): Date | null => {
        if (!value) return null
        if (value instanceof Date) return isValid(value) ? value : null
        if (typeof value === 'string') {
            const parsed = parseISO(value)
            return isValid(parsed) ? parsed : null
        }
        return null
    }
    const start = parseToDate(arrivalDate)
    const end = parseToDate(deadlineDate)

    if (!start || !end) return { sameDay: false }

    const startParts = getDateTimePartsWithTimezone(start, stateCode, latitude, longitude)
    const endParts = getDateTimePartsWithTimezone(end, stateCode, latitude, longitude)
    if (!startParts || !endParts) return { sameDay: false }

    // `windowLine` is produced for EVERY pair, same day or not — that is the whole point of it.
    //
    // The redesign used to fall back to the arrival alone whenever the two dates landed on different
    // days, and pushed the deadline into a tooltip. A dispatcher reads this line to know how late
    // they may arrive, so hiding the deadline behind a hover took away the one number the line
    // exists for, on exactly the loads with the widest windows. The frame's numeric date ("06/21")
    // is what buys the room back: it costs ~35px less than "Fri, Jun 21" and pays for the second time.
    const sameDayWindow = startParts.calendarDate === endParts.calendarDate
    const degenerate = start.getTime() === end.getTime()
    const startTimeWithZone = startParts.tz === endParts.tz ? startParts.timeLabel : `${startParts.timeLabel} ${startParts.tz}`
    const windowLine = degenerate
        ? `${startParts.numericDate}, ${startParts.timeLabel} ${startParts.tz}`
        : sameDayWindow
          ? `${startParts.numericDate}, ${startTimeWithZone} - ${endParts.timeLabel} ${endParts.tz}`
          : `${startParts.numericDate}, ${startTimeWithZone} - ${endParts.numericDate}, ${endParts.timeLabel} ${endParts.tz}`

    // Everything below keeps the shape the CLASSIC board reads. `sameDay` still means "same day", so
    // v1's `sameDay && singleLine` branch behaves exactly as before.
    if (!sameDayWindow) return { sameDay: false, windowLine }

    const singleLine = `${startParts.dateLabel} at ${startTimeWithZone} - ${endParts.timeLabel} ${endParts.tz}`

    // "Fri, Mar 28" -> "Mar 28". Split on the comma the label itself puts there rather than at a
    // fixed offset, so a label without a weekday passes through unharmed.
    const dayOnly = startParts.dateLabel.split(', ').slice(-1)[0]
    // Only a real meridiem is droppable. Matching on the suffix instead of slicing blindly keeps a
    // 24-hour label ("08:00" / "10:00", whose last two characters also match) intact.
    const meridiemOf = (time: string) => time.match(/ (AM|PM)$/)?.[1]
    const sharesMeridiem = Boolean(meridiemOf(startParts.timeLabel)) && meridiemOf(startParts.timeLabel) === meridiemOf(endParts.timeLabel)
    const startTime = startParts.tz !== endParts.tz ? startTimeWithZone : sharesMeridiem ? startParts.timeLabel.slice(0, -3) : startParts.timeLabel
    const compactLine = `${dayOnly}, ${startTime} - ${endParts.timeLabel} ${endParts.tz}`

    return { sameDay: true, singleLine, compactLine, windowLine }
}
/**
 * Gets the timezone abbreviation (e.g., EDT, PDT) for a given IANA timezone.
 * It uses the Intl API to extract abbreviation.
 */
const getTimezoneAbbreviation = (date: Date, state: string, latitude?: number | null, longitude?: number | null): string => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return ''
    }
    const timeZone = getTimezone(latitude, longitude, state)
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short',
    })

    const parts = formatter.formatToParts(date)
    const tzPart = parts.find((p) => p.type === 'timeZoneName')
    return tzPart?.value || ''
}

/**
 * Download selected proof photos.
 * @param load The ClientOngoingLoad object for naming context.
 * @param selectedImages Array of image URLs to download.
 */
export const downloadProofs = async (load: ClientOngoingLoad, selectedImages: string[]) => {
    if (!load || !selectedImages?.length) {
        console.error('No photos available')
        return
    }

    try {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
        const zipFileName = `proof_photos_${load.id}_${timestamp}.zip`
        const zip = new JSZip()
        let successfulImages = 0

        // Use the selected images or an empty array if none are selected
        const imagesToDownload = selectedImages.length > 0 ? selectedImages : []

        // If no images are selected and we have delivery statuses with photo proofs, collect them
        // if (imagesToDownload.length === 0 && load.deliveryStatus?.length > 0) {
        //     load.deliveryStatus.forEach((status) => {
        //         if (status.photoProofs && status.photoProofs.length > 0) {
        //             imagesToDownload.push(...status.photoProofs)
        //         }
        //     })
        // }

        const photoPromises = imagesToDownload.map(async (url, photoIndex) => {
            try {
                const response = await fetch(url)
                if (!response.ok) {
                    throw new Error(`Failed to fetch image ${photoIndex + 1}`)
                }
                const blob = await response.blob()
                let extension = 'jpg'
                if (blob.type) {
                    if (blob.type.includes('svg')) extension = 'svg'
                    else if (blob.type.includes('png')) extension = 'png'
                    else if (blob.type.includes('jpeg') || blob.type.includes('jpg')) extension = 'jpg'
                    else if (blob.type.includes('gif')) extension = 'gif'
                    else if (blob.type.includes('webp')) extension = 'webp'
                    else {
                        const parts = blob.type.split('/')
                        if (parts.length > 1) extension = parts[1].split(';')[0]
                    }
                }
                const fileName = `photo_${photoIndex + 1}.${extension}`
                zip.file(fileName, blob)
                return true
            } catch (error) {
                console.error(`Error downloading image ${photoIndex + 1}:`, error)
                return false
            }
        })

        const results = await Promise.all(photoPromises)
        successfulImages = results.filter(Boolean).length

        if (successfulImages === 0) {
            console.error('Failed to download any images')
            return
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' })
        const url = URL.createObjectURL(zipBlob)
        const a = document.createElement('a')
        a.href = url
        a.download = zipFileName
        document.body.appendChild(a)
        a.click()
        setTimeout(() => {
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        }, 100)
    } catch (error) {
        console.error('Error downloading proof photos:', error)
    }
}
const BOT_NOT_CONFIGURED = 'Bot username not configured'

export const getTelegramBotUsername = () => {
    const username = process.env.REACT_APP_TELEGRAM_BOT_USERNAME?.trim()

    if (!username) {
        return {
            username: undefined, // raw value (no @)
            formattedTelUsername: BOT_NOT_CONFIGURED,
            isConfigured: false,
        }
    }

    const formattedTelUsername = username.startsWith('@') ? username : `@${username}`

    return {
        username,
        formattedTelUsername, // always with @
        isConfigured: true,
    }
}
/**
 * Delete selected proof photos.
 * @param load The ClientOngoingLoad object containing the photos to delete.
 * @param photoProofsToDelete Array of photo proofs to delete.
 * @param onSuccess Optional callback function to execute after successful deletion.
 * @returns Promise that resolves to an object with success status and message.
 */
export const deleteProofs = async (load: ClientOngoingLoad, selectedImages: string[]) => {
    if (!load || !selectedImages?.length) {
        console.error('No photos available')
        return
    }

    try {
        // Extract just the URLs from the photo proofs
        const photoUrls = selectedImages.filter(Boolean) as string[]

        if (photoUrls.length === 0) {
            console.error('No valid photo URLs found to delete')
            return
        }

        // Call the API to delete the documents
        const response = await ongoingLoadsApi.deleteDeliveryDocuments(load.id.toString(), photoUrls)

        if (!response.success) {
            // Revert optimistic update if API call fails
            return {
                success: false,
                message: response.message || 'Failed to delete photos',
            }
        }
        return {
            success: true,
            message: 'Photos deleted successfully',
        }
    } catch (error) {
        const message = error instanceof Error ? error.message : 'An unknown error occurred while deleting photos'
        console.error('Error deleting photos:', error)
        return {
            success: false,
            message,
        }
    }
}

/**
 * Format a date to a user-friendly local time string.
 * @param dateInput The date value to format
 * @param monthLength Month format style
 * @returns Formatted date string or '--' for invalid input
 */
export const formatToUserLocalTime = (dateInput: Date | string | null | undefined, monthLength: 'short' | 'long' = 'short'): string => {
    if (!dateInput) return '--'
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput)
    if (isNaN(date.getTime())) return '--'

    const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        month: monthLength,
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
    }

    return date.toLocaleString('en-US', options)
}
export const formatDistance = (distanceMeters?: number) => {
    if (distanceMeters == null || Number.isNaN(distanceMeters)) return '--'
    const miles = distanceMeters / 1609.34
    // Show one decimal for short trips, integer otherwise
    if (miles < 10) return `${miles.toFixed(1)} mi`
    return `${Math.round(miles)} mi`
}

export const formatDuration = (durationHours?: number) => {
    if (durationHours == null || Number.isNaN(durationHours)) return '--'
    const totalMinutes = Math.round(durationHours * 60)
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

export const formatDateTime = (stop: IRoute) => {
    if (!stop) return '—'

    // Use the date and time directly from the stop object
    const dateStr = stop.date || '—'
    const timeStr = stop.time || '—'

    const state = stop.location?.state
    if (!state) {
        return `${dateStr} · ${timeStr}`
    }

    let tzAbbr = ''
    try {
        if (stop.arrivalDate) {
            // Extract coordinates from GeoPoint (GeoJSON format: [longitude, latitude])
            const coordinates = stop.location?.coordinates?.coordinates
            const longitude = coordinates?.[0]
            const latitude = coordinates?.[1]

            tzAbbr = getTimezoneAbbreviation(new Date(stop.arrivalDate), state, latitude, longitude)
        }
    } catch {
        // If we can't get timezone, just show without it
    }

    return tzAbbr ? `${dateStr} · ${timeStr} ${tzAbbr}` : `${dateStr} · ${timeStr}`
}

export const getStatusByTab = (tab: TabType): string[] => {
    switch (tab) {
        case 'history':
            return [OngoingLoadStatus.COMPLETED]
        case 'upcoming':
            return [OngoingLoadStatus.UPCOMING]
        case 'dispatched':
            return [OngoingLoadStatus.DISPATCHED]
        case 'in-transit':
            return [OngoingLoadStatus.IN_TRANSIT]
        case 'all-trips':
        default:
            return []
    }
}

/**
 * The one definition of "a driver this load can be dispatched to". Shared so the Dispatch gate, the
 * Telegram connect card and the driver-name column can't drift apart on what counts as active.
 */
export const isActiveDriverUser = (user?: ClientUser | null): boolean => {
    const status = user?.employmentStatus || DriverEmploymentStatus.ACTIVE
    return status === DriverEmploymentStatus.ACTIVE
}

/** The truck's drivers who are known, active users: the people a dispatch can be addressed to. */
const getActiveDriverUsers = (driverIds: ReadonlyArray<unknown>, users: ClientUser[]): ClientUser[] =>
    driverIds.flatMap((driverId) => {
        const user = users.find((u) => u.id === String(driverId))
        return user && isActiveDriverUser(user) ? [user] : []
    })

/** Whether the truck has anyone a dispatch can be addressed to. */
export const hasActiveDriver = (driverIds: ReadonlyArray<unknown> = [], users: ClientUser[] = []): boolean => getActiveDriverUsers(driverIds, users).length > 0

/** Whether any active driver on the truck has a Telegram group, i.e. can be sent a dispatch message. */
export const hasTelegramConnectedDriver = (driverIds: ReadonlyArray<unknown> = [], users: ClientUser[] = []): boolean =>
    getActiveDriverUsers(driverIds, users).some((user) => !!user.telegramGroup?.chatId)

export const getDriverNames = (driverIds: string[] = [], users: ClientUser[] = []) => {
    if (!driverIds.length) return 'No drivers'

    const names = driverIds
        .map((id) => {
            const user = users.find((u) => u.id === id)
            if (!isActiveDriverUser(user)) return null
            return user?.name || user?.email || null
        })
        .filter((x): x is string => !!x)

    return names.length ? names.join(', ') : 'No drivers'
}

export const getDispatcherNames = (driverIds: string[] = [], users: ClientUser[] = []) => {
    if (!driverIds.length) return 'No dispatchers'

    return driverIds
        .map((id) => {
            const user = users.find((u) => u.id === id)
            return user?.name || user?.email
        })
        .join(', ')
}

export const hasDriverDisconnectedFromTelegram = (truck: Truck, users: ClientUser[]): boolean => {
    const activeDrivers = (truck.drivers || [])
        .map((driverId) => users?.find((u) => u.id === String(driverId)))
        .filter((u): u is ClientUser => !!u && isActiveDriverUser(u))

    if (!activeDrivers.length) return false
    return activeDrivers.every((user) => !user.telegramGroup?.chatId)
}

export const isDriverHaveHazmatEndorsement = (truck: Truck, users: ClientUser[]): boolean => {
    const activeDrivers = (truck.drivers || [])
        .map((driverId) => users?.find((u) => u.id === String(driverId)))
        .filter((u): u is ClientUser => !!u && isActiveDriverUser(u))

    if (!activeDrivers.length) return true
    return activeDrivers.some((user) => !!user.cdlData?.permits?.hazmat)
}

export const formatDateTimeWithTimezoneETA = (date: Date | null, state: string, latitude?: number | null, longitude?: number | null) => {
    if (!date) return '--'
    if (isNaN(new Date(date).getTime())) return '--'

    const timezone = getTimezone(latitude, longitude, state)
    const inputDate = new Date(date)
    const parts = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
        timeZoneName: 'short',
    }).formatToParts(inputDate)

    const month = parts.find((p) => p.type === 'month')?.value || ''
    const day = parts.find((p) => p.type === 'day')?.value || ''
    const hour = parts.find((p) => p.type === 'hour')?.value || ''
    const minute = parts.find((p) => p.type === 'minute')?.value || ''
    const dayPeriod = parts.find((p) => p.type === 'dayPeriod')?.value || ''
    const tz = parts.find((p) => p.type === 'timeZoneName')?.value || ''

    return `${month} ${day}, ${hour}:${minute} ${dayPeriod} ${tz}`
}

/**
 * @param load - load with lastUpdated.time
 * @param thresholdMinutes - optional override; when not provided uses load.settings.notification.updateFrequency.threshold (e.g. when using global settings pass from application)
 */
export const getShipmentNextUpdateLabel = (load: ClientOngoingLoad, thresholdMinutes?: number): string | null => {
    const threshold = thresholdMinutes != null ? thresholdMinutes : load.settings?.notification?.updateFrequency?.threshold
    const lastTime = load.lastUpdated?.time
    if (!lastTime || threshold == null) return null
    const mins = Number(threshold)
    if (!Number.isFinite(mins) || mins <= 0) return null

    const lastMs = new Date(lastTime).getTime()
    if (Number.isNaN(lastMs)) return null
    // Time remaining until next update (mins → ms)
    const intervalMs = mins * 60 * 1000
    const elapsed = Date.now() - lastMs
    // When countdown expires, wrap around to show time until the next cycle
    const diffMs = intervalMs - (elapsed % intervalMs)
    if (diffMs <= 0) return null
    // Convert remaining time into minutes
    // If less than 1 minute remains, show "1m" instead of "0m"
    const totalMinutes = Math.max(1, Math.floor(diffMs / (60 * 1000)))
    const days = Math.floor(totalMinutes / (24 * 60))
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
    const minutes = totalMinutes % 60

    const parts: string[] = []
    if (days) parts.push(`${days}d`)
    if (hours) parts.push(`${hours}h`)
    if (minutes || !parts.length) parts.push(`${minutes}m`)

    return parts.join(' ')
}

/**
 * Parse coordinate string like "40.7128, -74.0060" or "40.7128 -74.0060"
 * @param input - Coordinate string input
 * @returns Object with lat and lng, or null if invalid
 */
export const parseCoordinates = (input: string): { lat: number; lng: number } | null => {
    const cleaned = input.trim().replace(/[,\s]+/g, ' ')
    const parts = cleaned.split(' ').filter(Boolean)
    if (parts.length !== 2) return null
    const lat = parseFloat(parts[0])
    const lng = parseFloat(parts[1])
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
    return { lat, lng }
}

/**
 * Format buffer minutes for display (e.g., "+15m", "+1h 30m")
 * @param bufferMinutes - Buffer time in minutes
 * @returns Formatted buffer string with "+" prefix, or empty string if no buffer
 */
export const formatBufferDisplay = (bufferMinutes?: number): string => {
    if (!bufferMinutes || bufferMinutes <= 0) return ''
    const formatted = formatDuration(bufferMinutes / 60) // Convert minutes to hours for formatDuration
    return `+${formatted}`
}
